import { motion } from "framer-motion";
import { Droplet, Flame, CheckCircle2, Star, Trophy } from "lucide-react";
import { useCountUp } from "../../hooks/useCountUp";
import { CalorieRing } from "./CalorieRing";
import { FREE_URL } from "../../lib/checkoutLinks";

const HERO_BG =
  "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&q=85";

const AVATARS = [
  "https://images.unsplash.com/photo-1725033489648-a819750348eb?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1790575188672-ee8d9d4e1c18?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1708011108776-45ad9e625269?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1658724683859-7f366527255d?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1707900285737-09a42c837118?crop=entropy&cs=srgb&fm=jpg&q=85",
];

const STATS = [
  { end: 10000, suffix: "+", label: "Members", testid: "hero-stat-members" },
  { end: 4.8, suffix: "", label: "Rating", testid: "hero-stat-rating", isDecimal: true },
  { end: 500, suffix: "+", label: "Indian Meals", testid: "hero-stat-meals" },
];

const Stat = ({ s }) => {
  const [ref, count] = useCountUp(s.isDecimal ? 48 : s.end, 1500);
  const display = s.isDecimal ? (count / 10).toFixed(1) : count.toLocaleString();
  return (
    <div ref={ref} data-testid={s.testid} className="flex flex-col">
      <span className="font-display text-3xl text-[#C6FF3D]">
        {display}
        {s.suffix}
      </span>
      <span className="text-[#A1A1AA] text-xs mt-1">{s.label}</span>
    </div>
  );
};

export const Hero = () => {
  return (
    <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 px-6 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-[position:70%_center]"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        role="img"
        aria-label="Indian athlete training in a dark, dramatically lit gym"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/75 to-[#0A0A0B]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-[#0A0A0B]/50" />
      <div className="absolute top-20 -left-40 w-96 h-96 bg-[#C6FF3D]/10 rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <span className="inline-flex items-center gap-2 bg-[#C6FF3D] text-black text-xs font-bold tracking-wide px-4 py-1.5 rounded-full mb-6">
            NEW: AI PHYSIQUE CHECK-INS + MONTHLY PROGRESS REPORTS
          </span>
          <h1
            data-testid="hero-heading"
            className="font-display text-[44px] leading-[1.05] sm:text-6xl lg:text-7xl text-white mb-6"
          >
            YOUR MEALS.
            <br />
            YOUR WORKOUTS.
            <br />
            <span className="text-[#C6FF3D]">PLANNED BY AI.</span>
          </h1>
          <p className="text-[#A1A1AA] text-base sm:text-lg max-w-md mb-8">
            AI diet and workout plans, a PR community, AI physique check-ins and monthly
            progress reports — with Coach Arjun.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <a
              href={FREE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
              className="tap-target flex items-center justify-center w-full sm:w-auto bg-[#C6FF3D] text-black font-semibold px-7 py-3.5 rounded-full hover:scale-105 hover:shadow-[0_0_30px_rgba(198,255,61,0.5)] transition-all duration-300"
            >
              Get My Free Plan
            </a>
            <a
              href="#how-it-works"
              data-testid="hero-cta-secondary"
              className="tap-target flex items-center justify-center w-full sm:w-auto border border-[#333336] text-white px-7 py-3.5 rounded-full hover:border-[#C6FF3D] hover:text-[#C6FF3D] transition-all duration-300"
            >
              See How It Works
            </a>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <div className="flex -space-x-3">
              {AVATARS.map((a, i) => (
                <img
                  key={i}
                  src={a}
                  alt="FORGE member"
                  className="w-9 h-9 rounded-full object-cover border-2 border-[#0A0A0B]"
                />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={13} className="text-[#C6FF3D]" fill="#C6FF3D" />
                ))}
              </div>
              <p className="text-[#A1A1AA] text-xs mt-0.5">10,000+ members training with FORGE</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-8 sm:gap-10">
            {STATS.map((s) => (
              <Stat key={s.label} s={s} />
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative flex justify-center lg:justify-end items-center"
        >
          <div className="absolute w-[300px] h-[300px] sm:w-[380px] sm:h-[380px] bg-[#C6FF3D]/25 rounded-full blur-[90px] animate-forge-glow" />
          <div
            data-testid="hero-phone-mockup"
            className="relative w-[200px] h-[400px] sm:w-[250px] sm:h-[510px] rounded-[36px] bg-[#141416] border-[7px] border-[#232326] shadow-2xl overflow-hidden"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 sm:w-20 h-3 sm:h-4 bg-[#0A0A0B] rounded-b-2xl z-20" />
            <div className="p-4 sm:p-5 pt-7 sm:pt-9 h-full flex flex-col gap-3 sm:gap-5">
              <div>
                <p className="text-[#A1A1AA] text-[10px] sm:text-xs">Today</p>
                <p className="text-white font-display text-sm sm:text-lg">Mon, 12 Oct</p>
              </div>
              <div className="flex items-center justify-between bg-[#0A0A0B] border border-[#232326] rounded-2xl p-3 sm:p-4">
                <CalorieRing percent={62} label="1240" sub="kcal left" />
                <div className="flex flex-col gap-2 items-end">
                  <div className="flex items-center gap-1 text-[#C6FF3D] text-[10px] sm:text-xs">
                    <Flame size={13} /> 1860 / 3100
                  </div>
                  <div className="flex items-center gap-1 text-[#A1A1AA] text-[10px] sm:text-xs">
                    <Droplet size={13} /> 5 / 8 glasses
                  </div>
                </div>
              </div>
              <div className="bg-[#0A0A0B] border border-[#232326] rounded-2xl p-3 sm:p-4">
                <p className="text-white text-[10px] sm:text-xs font-semibold mb-2 sm:mb-3">Meals</p>
                {["Poha + Peanuts", "Dal, Roti, Sabzi", "Paneer Bhurji"].map((m, i) => (
                  <div key={m} className="flex items-center gap-2 py-1 sm:py-1.5 text-[10px] sm:text-xs text-[#A1A1AA]">
                    <CheckCircle2
                      size={13}
                      className={i < 2 ? "text-[#C6FF3D]" : "text-[#3a3a3e]"}
                    />
                    <span className={i < 2 ? "text-white" : ""}>{m}</span>
                  </div>
                ))}
              </div>
              <div className="bg-[#0A0A0B] border border-[#C6FF3D]/40 rounded-2xl p-3 sm:p-4 relative overflow-hidden">
                <div className="flex items-center gap-2">
                  <Trophy size={14} className="text-[#C6FF3D]" />
                  <p className="text-[#C6FF3D] text-[10px] sm:text-xs font-semibold tracking-wide">NEW PR!</p>
                </div>
                <p className="text-white font-display text-sm sm:text-lg mt-1">Bench Press 100 kg</p>
                <span className="inline-block mt-1 bg-[#C6FF3D]/10 text-[#C6FF3D] text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full">
                  +5kg vs last time
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
